import React, { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'

export interface ContextMenuItem {
  id: string
  label: string
  icon?: React.ComponentType<{ className?: string }>
  onClick: () => void
  disabled?: boolean
  separator?: boolean
  submenu?: ContextMenuItem[]
  variant?: 'default' | 'danger' | 'warning'
  shortcut?: string
}

interface ContextMenuProps {
  x: number
  y: number
  items: ContextMenuItem[]
  onClose: () => void
  visible: boolean
}

export function ContextMenu({ x, y, items, onClose, visible }: ContextMenuProps) {
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const { target } = event
    if (!(target instanceof Element)) return
    const contextMenu = document.querySelector('[data-context-menu]')
    if (contextMenu && !contextMenu.contains(target)) {
      onClose()
    }
  }, [onClose])

  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const contextMenu = document.querySelector('[data-context-menu]')
    if (!contextMenu) return

    const focusableItems = contextMenu.querySelectorAll('button:not([disabled])')
    const { activeElement } = document
    const currentIndex = Array.from(focusableItems).findIndex((el) => el === activeElement)

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        const nextIndex = currentIndex < focusableItems.length - 1 ? currentIndex + 1 : 0
        if (focusableItems[nextIndex] instanceof HTMLElement) {
          focusableItems[nextIndex].focus()
        }
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableItems.length - 1
        if (focusableItems[prevIndex] instanceof HTMLElement) {
          focusableItems[prevIndex].focus()
        }
        break
      }
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.click()
        }
        break
    }
  }, [])

  useEffect(() => {
    if (visible) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
      document.addEventListener('keydown', handleKeyDown)
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscapeKey)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
    
    return undefined
  }, [visible, handleClickOutside, handleEscapeKey, handleKeyDown])

  if (!visible) return null

  // Calculate menu dimensions
  const menuWidth = 200
  const itemHeight = 28
  const separatorHeight = 8
  const menuPadding = 6
  
  let menuHeight = menuPadding * 2
  for (const item of items) {
    menuHeight += item.separator ? separatorHeight : itemHeight;
  }

  // Viewport positioning with better logic
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const margin = 8

  let adjustedX = x
  let adjustedY = y

  // Horizontal positioning - prefer right placement, fallback to left
  if (x + menuWidth + margin > viewportWidth) {
    adjustedX = Math.max(margin, x - menuWidth)
  }

  // Vertical positioning - prefer bottom placement, fallback to top
  if (y + menuHeight + margin > viewportHeight) {
    adjustedY = Math.max(margin, y - menuHeight)
  }

  // Ensure menu stays within viewport bounds
  adjustedX = Math.max(margin, Math.min(adjustedX, viewportWidth - menuWidth - margin))
  adjustedY = Math.max(margin, Math.min(adjustedY, viewportHeight - menuHeight - margin))

  const getItemVariantClasses = (variant?: string, disabled?: boolean) => {
    if (disabled) {
      return 'text-gray-400 dark:text-gray-500 cursor-not-allowed bg-transparent'
    }
    
    switch (variant) {
      case 'danger':
        return 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 hover:text-red-700 dark:hover:text-red-300'
      case 'warning':
        return 'text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/50 hover:text-orange-700 dark:hover:text-orange-300'
      case 'default':
      case undefined:
        return 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
      default:
        return 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
    }
  }

  const menuContent = (
    <div
      data-context-menu
      className="fixed z-[9999] min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg backdrop-blur-sm"
      style={{
        left: adjustedX,
        top: adjustedY,
      }}
    >
      <div className="py-1">
        {items.map((item) => (
          <React.Fragment key={item.id}>
            {item.separator ? (
              <div className="mx-2 my-0.5 border-t border-gray-200 dark:border-gray-600" />
            ) : (
              <button type="button"
                className={clsx(
                  'w-full flex items-center px-2.5 py-1.5 text-xs font-medium transition-all duration-150 ease-in-out',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset focus:bg-blue-50 dark:focus:bg-blue-950/50',
                  getItemVariantClasses(item.variant, item.disabled)
                )}
                onClick={() => {
                  if (!item.disabled) {
                    item.onClick()
                    onClose()
                  }
                }}
                disabled={item.disabled}
                role="menuitem"
                tabIndex={item.disabled ? -1 : 0}
              >
                {item.icon && (
                  <item.icon className="w-3.5 h-3.5 mr-2.5 flex-shrink-0" />
                )}
                <span className="flex-1 text-left truncate">{item.label}</span>
                {item.shortcut && (
                  <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-mono">
                    {item.shortcut}
                  </span>
                )}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )

  return createPortal(menuContent, document.body)
}